import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_REQUEST_TIMEOUT = 10000;
const DELAYED_CART_RECONCILIATION_MS = 1500;

function getUnconfirmedCartChangeMessage() {
  return (
    Theme.translations?.cart_change_unconfirmed ||
    "We couldn't confirm that cart change. Review your cart before trying again."
  );
}

/**
 * Fetches and parses a cart endpoint without ever retrying a mutation.
 * @param {string} url
 * @param {RequestInit} options
 * @param {string} requestName
 */
async function fetchCartJSON(url, options, requestName) {
  const abortController = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    abortController.abort();
  }, CART_REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, { ...options, signal: abortController.signal });
    const responseText = await response.text();
    let data;

    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error(`${requestName} returned invalid JSON (${response.status})`);
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error(`${requestName} returned an invalid response`);
    }

    return { data, response };
  } catch (error) {
    if (timedOut) throw new Error(`${requestName} timed out`);
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

/** @param {any} cart */
function validateCart(cart) {
  if (
    !Array.isArray(cart?.items) ||
    !Number.isFinite(cart?.item_count) ||
    !Number.isFinite(cart?.total_price) ||
    typeof cart?.currency !== 'string' ||
    !cart.items.every(
      /** @param {any} item */
      (item) =>
        typeof item?.key === 'string' &&
        (typeof item?.variant_id === 'number' || typeof item?.variant_id === 'string') &&
        Number.isFinite(item?.quantity) &&
        Number.isFinite(item?.final_line_price)
    )
  ) {
    throw new Error('Cart response was missing required data');
  }

  return cart;
}

/**
 * @param {any} first
 * @param {any} second
 */
function cartSnapshotsMatch(first, second) {
  if (
    first.item_count !== second.item_count ||
    first.total_price !== second.total_price ||
    first.items.length !== second.items.length
  ) {
    return false;
  }

  return first.items.every(
    /** @param {any} item @param {number} index */
    (item, index) => {
      const otherItem = second.items[index];
      return (
        item.key === otherItem?.key &&
        item.quantity === otherItem.quantity &&
        item.final_line_price === otherItem.final_line_price
      );
    }
  );
}

/**
 * Returns section HTML only when it has the wrapper morphSection expects.
 * @param {any} sections
 * @param {string} sectionId
 * @param {number} [expectedItemCount]
 */
function getValidSectionHTML(sections, sectionId, expectedItemCount) {
  const html = sections?.[sectionId];
  if (typeof html !== 'string' || html.trim() === '') return null;

  const responseDocument = new DOMParser().parseFromString(html, 'text/html');
  if (!responseDocument.getElementById(buildSectionSelector(sectionId))) return null;
  if (expectedItemCount !== undefined && getSectionItemCount(html, sectionId) !== expectedItemCount) return null;
  return html;
}

/**
 * @param {string} html
 * @param {string} sectionId
 */
function getSectionItemCount(html, sectionId) {
  const responseDocument = new DOMParser().parseFromString(html, 'text/html');
  const section = responseDocument.getElementById(buildSectionSelector(sectionId));
  if (!section) return null;

  const count = Number.parseInt(section.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
  if (Number.isFinite(count) && count >= 0) return count;
  if (section.querySelector('[data-cart-drawer-empty], .cart-page--empty')) return 0;
  return null;
}

/**
 * @param {any} data
 * @param {Response} response
 */
function getExplicitCartErrorMessage(data, response) {
  const bodyStatus = Number(data?.status);
  const isShopifyValidationResponse =
    (bodyStatus === 400 || bodyStatus === 422) && response.status === bodyStatus;
  const message =
    (typeof data?.description === 'string' && data.description.trim()) ||
    (typeof data?.message === 'string' && data.message.trim()) ||
    (typeof data?.errors === 'string' && data.errors.trim()) ||
    '';

  if (isShopifyValidationResponse && message) return message;
  if (!response.ok || data?.status) {
    throw new Error(
      `Cart update returned an untrusted error response (${response.status} ${response.statusText})`
    );
  }

  return null;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement | undefined} cartMutationErrorContainer - The cart-level mutation error container.
 * @property {HTMLElement | undefined} cartMutationErrorAlert - The cart-level mutation error alert.
 * @property {HTMLElement | undefined} cartMutationError - The cart-level mutation error text.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  #historyRestorePending = false;

  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<void> | null} */
  #pendingCartFetch = null;

  /** @type {number} */
  #cartFetchRequestedRevision = 0;

  /** @type {number} */
  #cartFetchCompletedRevision = 0;

  /** @type {any} */
  #latestCartData = null;

  /** @type {number} */
  #cartUpdateRevision = 0;

  /** @type {Promise<string> | null} */
  #pendingCartReconciliation = null;

  /** @type {boolean} */
  #cartMutationInFlight = false;

  /** @type {Array<{control: HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement, wasDisabled: boolean, wasFocused: boolean}>} */
  #disabledControlStates = [];

  /** @type {any} */
  #owedCartReconciliation = null;

  /** @type {number | undefined} */
  #owedCartReconciliationTimer;

  /** @type {Promise<void> | null} */
  #pendingOwedCartReconciliation = null;

  /** @type {number} */
  #pendingCartUpdateEvents = 0;

  /** @type {number} */
  #cartDataReconciliationRevision = 0;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(async ({ detail }) => {
        if (!external) return;
        const sectionsHtml = getValidSectionHTML(detail?.sections, this.sectionId);
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = getValidSectionHTML(sections, this.sectionId);
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    if (CartItemsComponent.#getNavigationType() === 'back_forward') this.#restoreHistoryCart();
    this.#runOwedCartReconciliation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#owedCartReconciliationTimer);
    this.#owedCartReconciliationTimer = undefined;

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    if (!event.persisted && CartItemsComponent.#getNavigationType() !== 'back_forward') return;
    this.#restoreHistoryCart();
  };

  #restoreHistoryCart() {
    if (!this.dataset.sectionId) return;
    this.#historyRestorePending = true;
    this.#queueOwedCartReconciliation();
  }

  static #getNavigationType() {
    const entry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    return entry?.entryType === 'navigation' && 'type' in entry ? entry.type : undefined;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (this.#cartMutationInFlight) {
      const quantityInput = this.refs.quantitySelectors[line - 1]?.querySelector('input');
      if (quantityInput) quantityInput.value = quantityInput.defaultValue;
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (this.#cartMutationInFlight) return;
    if (this.#owedCartReconciliation) this.#queueOwedCartReconciliation({ delay: DELAYED_CART_RECONCILIATION_MS });
    const { line, quantity } = config;
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    const removalFocusState = quantity === 0 ? this.#captureRemovalFocus(line) : null;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    for (const sectionId of sectionsToUpdate) sectionRenderer.abortRender(sectionId);

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const revision = ++this.#cartUpdateRevision;
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.#cartMutationInFlight = true;
    this.setAttribute('aria-busy', 'true');
    this.#disableCartItems();
    cartTotal?.shimmer();
    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => { completeQuantityUpdate = () => resolve(); });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);
    let quantityVerification = Promise.resolve();

    try {
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: lineId, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const requestConfig = fetchConfig('json', { body });
      const { data, response } = await fetchCartJSON(
        `${Theme.routes.cart_change_url}`,
        { ...requestConfig, cache: 'no-store', credentials: 'same-origin' },
        'Cart update'
      );
      const errorMessage = getExplicitCartErrorMessage(data, response);

      if (errorMessage) {
        this.#showCartError(lineId, line, errorMessage);
        this.dispatchEvent(new CartErrorEvent({ error: errorMessage, code: 'INVALID' }));
        await this.#reconcileFailedCartMutation(deferredUpdatePromise, new Error(errorMessage), {
          lineId,
          line,
          message: errorMessage,
        });
        return;
      }

      const responseCart = validateCart(data);
      const responseSectionHTML = getValidSectionHTML(
        responseCart.sections,
        this.sectionId,
        responseCart.item_count
      );
      if (revision === this.#cartUpdateRevision) this.#updateQuantitySelectors(responseCart);
      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(responseCart),
        detail: {
          sections: responseSectionHTML ? responseCart.sections : undefined,
          items: responseCart.items,
          itemCount: responseCart.item_count,
          source: 'cart-items-component',
          didError: false,
        },
      });

      const hadPendingReconciliation = Boolean(this.#pendingCartReconciliation);
      let renderedResponseSection = false;
      if (revision === this.#cartUpdateRevision && responseSectionHTML) {
        try {
          sectionRenderer.abortRender(this.sectionId);
          await morphSection(this.sectionId, responseSectionHTML, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
          renderedResponseSection = true;
        } catch (error) {
          console.warn('[cart-items] Could not render the cart update response:', error);
        }
      }

      if (revision !== this.#cartUpdateRevision) return;
      if (!renderedResponseSection || hadPendingReconciliation || this.#pendingCartReconciliation) {
        try {
          await this.#renderCurrentCartSection();
        } catch (error) {
          if (error?.name !== 'AbortError') {
            console.warn('[cart-items] Could not reconcile the confirmed cart update:', error);
            this.dispatchEvent(
              new CartErrorEvent({
                error: 'The cart was updated, but the latest cart view could not be loaded',
                code: 'SERVICE_UNAVAILABLE',
              })
            );
          }
        }
      }
      this.#updateCartQuantitySelectorButtonStates();

      quantityVerification = this.fetchCartData()
        .then(async (currentCart) => {
          if (!this.isConnected || revision !== this.#cartUpdateRevision) return;
          this.#updateQuantitySelectors(currentCart);
          const snapshotsMatch = cartSnapshotsMatch(responseCart, currentCart);
          if (snapshotsMatch && renderedResponseSection) return;

          if (!snapshotsMatch) this.#updateCartCount(currentCart.item_count, revision);
          try {
            if (revision !== this.#cartUpdateRevision) return;
            await this.#renderCurrentCartSection();
            if (!this.isConnected || revision !== this.#cartUpdateRevision) return;
            this.#updateCartQuantitySelectorButtonStates();
          } catch (error) {
            if (error?.name !== 'AbortError') {
              console.warn('[cart-items] Could not render a newer cart snapshot:', error);
            }
          }
        })
        .catch((error) => {
          console.warn('[cart-items] Could not verify the confirmed cart update:', error);
        });
    } catch (error) {
      console.error(error);
      resetShimmer(this);
      const errorMessage = getUnconfirmedCartChangeMessage();
      this.#showCartError(lineId, line, errorMessage);

      this.dispatchEvent(
        new CartErrorEvent({
          error: errorMessage,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#queueOwedCartReconciliation({
        delay: DELAYED_CART_RECONCILIATION_MS,
        errorContext: { lineId, line, message: errorMessage },
        removalFocusState,
      });
      await this.#reconcileFailedCartMutation(deferredUpdatePromise, error, {
        lineId,
        line,
        message: errorMessage,
      });
    } finally {
      try {
        resetShimmer(this);
        this.#enableCartItems();
        this.#cartMutationInFlight = false;
        this.removeAttribute('aria-busy');
        this.#runOwedCartReconciliation();
        if (quantity === 0) this.#restoreRemovalFocus(removalFocusState);
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      } finally {
        void quantityVerification.finally(() => {
          CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
          completeQuantityUpdate();
        });
      }
    }
  }

  /** @param {boolean} [injectStylesheet=false] */
  async #renderCurrentCartSection(injectStylesheet = false) {
    const renderPromise = sectionRenderer.renderSection(this.sectionId, {
      cache: false,
      mode: this.isDrawer ? 'hydration' : 'full',
      injectStylesheet,
    });

    const trackedPromise = renderPromise.finally(() => {
      if (this.#pendingCartReconciliation === trackedPromise) {
        this.#pendingCartReconciliation = null;
      }
    });
    this.#pendingCartReconciliation = trackedPromise;
    return trackedPromise;
  }

  /**
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredUpdatePromise
   * @param {unknown} originalError
   * @param {{lineId: string, line: number, message: string}} errorContext
   */
  async #reconcileFailedCartMutation(deferredUpdatePromise, originalError, errorContext) {
    const [cartResult, sectionResult] = await Promise.allSettled([
      this.fetchCartData(),
      this.#renderCurrentCartSection(),
    ]);

    if (cartResult.status === 'rejected') {
      console.warn('[cart-items] Could not refresh cart data during reconciliation:', cartResult.reason);
    }
    if (sectionResult.status === 'rejected' && sectionResult.reason?.name !== 'AbortError') {
      console.warn('[cart-items] Could not refresh the cart section during reconciliation:', sectionResult.reason);
    }

    const currentCart = cartResult.status === 'fulfilled' ? cartResult.value : null;
    const sectionItemCount =
      sectionResult.status === 'fulfilled' ? getSectionItemCount(sectionResult.value, this.sectionId) : null;
    const itemCount = currentCart?.item_count ?? sectionItemCount;

    this.#showCartError(errorContext.lineId, errorContext.line, errorContext.message, false);

    if (itemCount === null || itemCount === undefined) {
      deferredUpdatePromise.reject(originalError);
      return;
    }

    try {
      if (currentCart) this.#updateQuantitySelectors(currentCart);

      deferredUpdatePromise.resolve({
        cart: currentCart ? CartLinesUpdateEvent.createCartFromAjaxResponse(currentCart) : null,
        detail: {
          items: currentCart?.items,
          itemCount,
          source: 'cart-items-component',
          didError: true,
        },
      });
    } catch (error) {
      console.warn('[cart-items] Could not apply reconciled cart data:', error);
      deferredUpdatePromise.reject(originalError);
    }
  }

  /**
   * Shows an error on the initiating line after any section morph. The stable
   * line key prevents a shifted row index from assigning the message to the
   * wrong item. If the line is gone, announce the cart-level outcome instead.
   * @param {string} lineId
   * @param {number} originalLine
   * @param {string} message
   * @param {boolean} [announce=true]
   */
  #showCartError(lineId, originalLine, message, announce = true) {
    const rows = this.refs.cartItemRows ?? [];
    const cartItemRow = lineId
      ? rows.find((row) => row.dataset.key === lineId)
      : rows[originalLine - 1];
    const quantityInput = cartItemRow?.querySelector('input');

    if (quantityInput instanceof HTMLInputElement) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = cartItemRow?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = cartItemRow?.querySelector('.cart-items__error');

    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      const alert = cartItemError.closest('[role="alert"]');
      if (alert instanceof HTMLElement) {
        if (announce) alert.removeAttribute('aria-live');
        else alert.setAttribute('aria-live', 'off');
      }
      cartItemError.textContent = message;
      cartItemErrorContainer.classList.remove('hidden');
      return;
    }

    if (this.#showCartMutationError(message, announce)) return;

    if (!announce) return;
    const liveRegion = document.querySelector('[data-testid="cart-count-live-region"]');
    if (liveRegion) liveRegion.textContent = message;
  }

  /**
   * @param {string} message
   * @param {boolean} announce
   */
  #showCartMutationError(message, announce) {
    const { cartMutationError, cartMutationErrorContainer, cartMutationErrorAlert } = this.refs;
    if (
      cartMutationError instanceof HTMLElement &&
      cartMutationErrorContainer instanceof HTMLElement &&
      cartMutationErrorAlert instanceof HTMLElement
    ) {
      if (announce) cartMutationErrorAlert.removeAttribute('aria-live');
      else cartMutationErrorAlert.setAttribute('aria-live', 'off');
      cartMutationError.textContent = message;
      cartMutationErrorContainer.classList.remove('hidden');
      return true;
    }

    return false;
  }

  /**
   * Restores an add error after the drawer morphs. Prefer the same
   * complementary product when it is still recommended; otherwise use the
   * cart-level error surface.
   * @param {string | undefined} productId
   * @param {string} message
   */
  #restoreProductAddError(productId, message) {
    const cards = Array.from(this.querySelectorAll('.real-cart-drawer-complementary__item'));
    const productCard = cards.find(
      (card) => card instanceof HTMLElement && card.dataset.productId === productId
    );
    const productError = productCard?.querySelector('[ref="addToCartTextError"]');

    if (productError instanceof HTMLElement) {
      productError.setAttribute('aria-live', 'off');
      for (const childNode of Array.from(productError.childNodes)) {
        if (childNode.nodeType === Node.TEXT_NODE) childNode.remove();
      }
      productError.appendChild(document.createTextNode(message));
      productError.classList.remove('hidden');
      return;
    }

    this.#showCartMutationError(message, false);
  }

  /**
   * Coalesces ambiguous, deferred, and malformed-event recovery into one
   * authoritative trailing refresh. New activity postpones this work without
   * cancelling the obligation.
   * @param {any} [options]
   */
  #queueOwedCartReconciliation(options = {}) {
    const pending = this.#owedCartReconciliation;
    this.#owedCartReconciliation = {
      notBefore: Math.max(pending?.notBefore ?? 0, Date.now() + (options.delay ?? 0)),
      retriesRemaining: pending?.retriesRemaining ?? 1,
      feedback: options.feedback ?? pending?.feedback,
      errorContext: options.errorContext ?? pending?.errorContext,
      removalFocusState: options.removalFocusState ?? pending?.removalFocusState,
      complementaryFocusState: options.complementaryFocusState ?? pending?.complementaryFocusState,
    };
    this.#runOwedCartReconciliation();
  }

  #runOwedCartReconciliation() {
    const request = this.#owedCartReconciliation;
    if (!request || !this.isConnected || this.#cartMutationInFlight) return;
    if (this.#pendingCartUpdateEvents > 0 || this.#pendingOwedCartReconciliation) return;

    const delay = request.notBefore - Date.now();
    if (delay > 0) {
      if (this.#owedCartReconciliationTimer !== undefined) clearTimeout(this.#owedCartReconciliationTimer);
      this.#owedCartReconciliationTimer = setTimeout(() => {
        this.#owedCartReconciliationTimer = undefined;
        this.#runOwedCartReconciliation();
      }, delay);
      return;
    }

    const revision = this.#cartUpdateRevision;
    const reconciliationRevision = ++this.#cartDataReconciliationRevision;
    const reconciliation = (async () => {
      try {
        const cart = await this.fetchCartData();
        if (request !== this.#owedCartReconciliation) return;
        if (!this.#isCartReconciliationCurrent(revision, reconciliationRevision)) return;

        for (const peer of document.querySelectorAll('cart-items-component')) {
          if (peer !== this && 'reconcileCartData' in peer && typeof peer.reconcileCartData === 'function') {
            void peer.reconcileCartData(cart).catch((error) => {
              if (error?.name !== 'AbortError') console.warn('[cart-items] Could not reconcile a peer cart surface:', error);
            });
          }
        }
        const applied = await this.#applyReconciledCartData(
          cart,
          request.feedback,
          revision,
          reconciliationRevision,
          request.complementaryFocusState ?? this.#captureComplementaryFocus()
        );
        if (!applied || request !== this.#owedCartReconciliation) return;

        if (request.errorContext) {
          this.#showCartError(
            request.errorContext.lineId,
            request.errorContext.line,
            request.errorContext.message,
            false
          );
        }
        this.#restoreRemovalFocus(request.removalFocusState ?? null);
        this.#owedCartReconciliation = null;
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-items] Could not complete authoritative cart reconciliation:', error);
        }
        if (request !== this.#owedCartReconciliation) return;
        this.#owedCartReconciliation = request.retriesRemaining
          ? { ...request, notBefore: Date.now() + DELAYED_CART_RECONCILIATION_MS, retriesRemaining: 0 }
          : null;
      }
    })();
    const trackedReconciliation = reconciliation.finally(() => {
      if (this.#pendingOwedCartReconciliation === trackedReconciliation) {
        this.#pendingOwedCartReconciliation = null;
      }
      this.#runOwedCartReconciliation();
    });
    this.#pendingOwedCartReconciliation = trackedReconciliation;
  }

  #isCartReconciliationCurrent(revision, reconciliationRevision) {
    return (
      this.isConnected &&
      revision === this.#cartUpdateRevision &&
      reconciliationRevision === this.#cartDataReconciliationRevision
    );
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;
    const eventPromise = event?.promise;
    if (!eventPromise || typeof eventPromise.then !== 'function') {
      this.#queueOwedCartReconciliation({ complementaryFocusState: this.#captureComplementaryFocus() });
      return;
    }
    if (this.#owedCartReconciliation) this.#queueOwedCartReconciliation({ delay: DELAYED_CART_RECONCILIATION_MS });
    const complementaryFocusState = this.#captureComplementaryFocus();
    const revision = ++this.#cartUpdateRevision;
    this.#pendingCartUpdateEvents += 1;

    Promise.resolve(eventPromise)
      .then(
        async ({ cart, detail }) => {
          if (revision !== this.#cartUpdateRevision) return;

          if (detail?.didError && detail?.cartChanged === false && detail?.outcomeAmbiguous !== true) {
            if (detail?.items) this.#updateQuantitySelectors({ items: detail.items });
            this.#updateCartQuantitySelectorButtonStates();
            return;
          }

          const sections = detail?.sections;
          const eventItemCount = cart?.totalQuantity ?? detail?.itemCount;
          const cartItemsHtml = getValidSectionHTML(
            sections,
            this.sectionId,
            Number.isFinite(eventItemCount) ? Number(eventItemCount) : undefined
          );
          // Animate empty → non-empty in the drawer (possible in squeeze mode
          // where the page is interactive alongside the open drawer). This also
          // needs the response stylesheet because it adds the cart summary markup.
          const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
          /** @type {'hydration' | 'full'} */
          const mode = this.isDrawer ? 'hydration' : 'full';
          const morphOptions = {
            mode,
            injectStylesheet: wasEmptyCartDrawer,
          };

          const hadPendingReconciliation = Boolean(this.#pendingCartReconciliation);
          let renderedResponseSection = false;
          if (cartItemsHtml && revision === this.#cartUpdateRevision) {
            const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

            try {
              if (wasEmptyCartDrawer) {
                let morphPromise = Promise.resolve();
                await startViewTransition(
                  () => {
                    if (revision !== this.#cartUpdateRevision) return;
                    sectionRenderer.abortRender(this.sectionId);
                    morphPromise = morphSection(this.sectionId, cartItemsHtml, morphOptions);
                  },
                  ['fill-cart-drawer']
                );
                await morphPromise;
              } else {
                sectionRenderer.abortRender(this.sectionId);
                await morphSection(this.sectionId, cartItemsHtml, morphOptions);
              }
              renderedResponseSection = revision === this.#cartUpdateRevision;
            } catch (error) {
              console.warn('[cart-items] Could not render cart event sections:', error);
            }

            // Animate newly added rows (reverse of the remove animation).
            if (renderedResponseSection && !wasEmptyCartDrawer && !prefersReducedMotion()) {
              for (const row of this.refs.cartItemRows ?? []) {
                if (!existingKeys.has(row.dataset.key)) {
                  row.classList.add('adding');
                  onAnimationEnd(row, () => row.classList.remove('adding'));
                }
              }
            }

            // Update button states for all cart quantity selectors after morph
            this.#updateCartQuantitySelectorButtonStates();
          }

          if (revision !== this.#cartUpdateRevision) return;
          if (!renderedResponseSection || hadPendingReconciliation || this.#pendingCartReconciliation) {
            try {
              await this.#renderCurrentCartSection(wasEmptyCartDrawer);
              if (revision !== this.#cartUpdateRevision) return;
              renderedResponseSection = true;
            } catch (error) {
              if (error?.name !== 'AbortError') {
                console.warn('[cart-items] Could not reconcile the cart section:', error);
              }
            }
          }
          this.#updateCartQuantitySelectorButtonStates();
          if (renderedResponseSection && typeof detail?.errorMessage === 'string') {
            this.#restoreProductAddError(detail.productId, detail.errorMessage);
          }
          if (renderedResponseSection) this.#restoreComplementaryFocus(complementaryFocusState);
        },
        async (error) => {
          if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
          if (revision !== this.#cartUpdateRevision) return;
          const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
          try {
            await this.#renderCurrentCartSection(wasEmptyCartDrawer);
            if (revision !== this.#cartUpdateRevision) return;
            this.#restoreComplementaryFocus(complementaryFocusState);
          } catch (sectionError) {
            if (sectionError?.name !== 'AbortError') {
              console.warn('[cart-items] Could not reconcile the cart section:', sectionError);
            }
          }
        }
      )
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Could not process cart event:', error);
      })
      .finally(() => {
        this.#pendingCartUpdateEvents = Math.max(0, this.#pendingCartUpdateEvents - 1);
        this.#runOwedCartReconciliation();
      });
  };

  #captureComplementaryFocus() {
    if (!this.isDrawer) return null;

    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLElement)) return null;

    const card = activeElement.closest('.real-cart-drawer-complementary__item');
    if (!(card instanceof HTMLElement) || !this.contains(card)) return null;

    const cards = Array.from(this.querySelectorAll('.real-cart-drawer-complementary__item'));
    return {
      activeElement,
      productId: card.dataset.productId ?? '',
      cardIndex: cards.indexOf(card),
    };
  }

  /**
   * @param {{activeElement: HTMLElement, productId: string, cardIndex: number} | null} focusState
   */
  #restoreComplementaryFocus(focusState) {
    if (!focusState) return;

    const currentFocusedCard = focusState.activeElement.isConnected
      ? focusState.activeElement.closest('.real-cart-drawer-complementary__item')
      : null;
    const focusedNodeWasReused =
      focusState.activeElement.isConnected &&
      (!(currentFocusedCard instanceof HTMLElement) || currentFocusedCard.dataset.productId !== focusState.productId);
    if (
      focusState.activeElement.isConnected &&
      !focusedNodeWasReused &&
      document.activeElement === focusState.activeElement
    ) {
      return;
    }
    if (
      document.activeElement &&
      document.activeElement !== document.body &&
      document.activeElement !== focusState.activeElement
    ) {
      return;
    }

    const dialog = this.closest('dialog');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const cards = Array.from(this.querySelectorAll('.real-cart-drawer-complementary__item'));
    const matchingCard = cards.find(
      (card) => card instanceof HTMLElement && card.dataset.productId === focusState.productId
    );
    const nextCard = matchingCard ?? cards[Math.min(Math.max(focusState.cardIndex, 0), cards.length - 1)];
    const nextRecommendationAction =
      nextCard?.querySelector('.real-cart-drawer-complementary__button:not([disabled])') ??
      nextCard?.querySelector('button:not([disabled])') ??
      nextCard?.querySelector('.real-cart-drawer-complementary__title') ??
      nextCard?.querySelector('.real-cart-drawer-complementary__image-link') ??
      nextCard?.querySelector('a[href]:not(.visually-hidden)');
    const checkout = this.querySelector('#checkout:not([disabled])');
    const closeButton = dialog.querySelector('.theme-drawer__close-button:not([disabled])');
    const heading = document.getElementById('cart-drawer-heading');
    const focusTarget = nextRecommendationAction ?? checkout ?? closeButton ?? heading;

    if (!(focusTarget instanceof HTMLElement)) return;
    if (focusTarget === heading && !focusTarget.hasAttribute('tabindex')) {
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), { once: true });
    }
    try {
      focusTarget.focus({ preventScroll: true });
    } catch {
      focusTarget.focus();
    }
  }

  /** @param {number} line */
  #captureRemovalFocus(line) {
    const activeElement = document.activeElement;
    const row = this.refs.cartItemRows[line - 1];
    if (!(activeElement instanceof HTMLElement) || !row?.contains(activeElement)) return null;

    return { activeElement, rowIndex: line - 1 };
  }

  /** @param {{activeElement: HTMLElement, rowIndex: number} | null} focusState */
  #restoreRemovalFocus(focusState) {
    if (!focusState) return;
    if (focusState.activeElement.isConnected && document.activeElement === focusState.activeElement) return;
    if (
      document.activeElement &&
      document.activeElement !== document.body &&
      document.activeElement !== focusState.activeElement
    ) {
      return;
    }

    const dialog = this.closest('dialog');
    if (this.isDrawer && (!(dialog instanceof HTMLDialogElement) || !dialog.open)) return;

    if (focusState.activeElement.isConnected && !focusState.activeElement.matches(':disabled')) {
      try {
        focusState.activeElement.focus({ preventScroll: true });
      } catch {
        focusState.activeElement.focus();
      }
      return;
    }

    const rows = this.refs.cartItemRows ?? [];
    const nextRow = rows[Math.min(focusState.rowIndex, rows.length - 1)];
    const nextRowAction =
      nextRow?.querySelector('.cart-items__remove:not([hidden]):not([disabled])') ??
      nextRow?.querySelector('button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]');
    const checkout = this.querySelector('#checkout:not([disabled])');
    const heading = this.isDrawer
      ? document.getElementById('cart-drawer-heading')
      : this.querySelector('.cart-title h1');
    const focusTarget = nextRowAction ?? checkout ?? heading;

    if (!(focusTarget instanceof HTMLElement)) return;
    if (focusTarget === heading && !focusTarget.hasAttribute('tabindex')) {
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), { once: true });
    }
    try {
      focusTarget.focus({ preventScroll: true });
    } catch {
      focusTarget.focus();
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.#disabledControlStates = [];

    for (const control of this.querySelectorAll('button, input, select, textarea')) {
      if (
        !(control instanceof HTMLButtonElement) &&
        !(control instanceof HTMLInputElement) &&
        !(control instanceof HTMLSelectElement) &&
        !(control instanceof HTMLTextAreaElement)
      ) {
        continue;
      }

      this.#disabledControlStates.push({
        control,
        wasDisabled: control.disabled,
        wasFocused: control === document.activeElement,
      });
      control.setAttribute('data-cart-mutation-disabled', 'true');
      control.disabled = true;
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    /** @type {HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | undefined} */
    let focusTarget;

    for (const { control, wasDisabled, wasFocused } of this.#disabledControlStates) {
      if (!control.isConnected || control.getAttribute('data-cart-mutation-disabled') !== 'true') continue;
      control.removeAttribute('data-cart-mutation-disabled');
      control.disabled = wasDisabled;
      if (wasFocused && !control.disabled) focusTarget = control;
    }
    this.#disabledControlStates = [];
    this.#updateCartQuantitySelectorButtonStates();

    if (focusTarget && (document.activeElement === document.body || document.activeElement === null)) {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch {
        focusTarget.focus();
      }
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number | string, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!Array.isArray(updatedCart.items)) return;

    /** @type {Map<string, number>} */
    const quantitiesByVariant = new Map();
    for (const item of updatedCart.items) {
      if (
        (typeof item?.variant_id !== 'number' && typeof item?.variant_id !== 'string') ||
        !Number.isFinite(item?.quantity)
      ) continue;
      const variantId = item.variant_id.toString();
      quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) ?? 0) + item.quantity);
    }

    for (const [variantId, quantity] of quantitiesByVariant) {
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * @param {number} itemCount
   * @param {number} revision
   */
  #updateCartCount(itemCount, revision) {
    if (!this.isConnected || revision !== this.#cartUpdateRevision) return;

    for (const cartIcon of document.querySelectorAll('cart-icon')) {
      if ('renderCartBubble' in cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        void cartIcon.renderCartBubble(itemCount, false);
      }
    }

    const liveRegion = document.querySelector('[data-testid="cart-count-live-region"]');
    const message = `${Theme.translations.cart_count}: ${itemCount}`;
    if (liveRegion && liveRegion.textContent !== message) liveRegion.textContent = message;
  }

  /**
   * @param {any} cart
   * @param {{productId: string | undefined, errorMessage: string} | undefined} [feedback]
   * @param {number} revision
   * @param {number} reconciliationRevision
   * @param {{activeElement: HTMLElement, productId: string, cardIndex: number} | null} complementaryFocusState
   * @returns {Promise<boolean>}
   */
  async #applyReconciledCartData(cart, feedback, revision, reconciliationRevision, complementaryFocusState) {
    if (!this.#isCartReconciliationCurrent(revision, reconciliationRevision)) return false;
    const currentCart = validateCart(cart);
    this.#updateQuantitySelectors(currentCart);
    this.#updateCartCount(currentCart.item_count, revision);
    const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
    await this.#renderCurrentCartSection(wasEmptyCartDrawer);
    if (!this.#isCartReconciliationCurrent(revision, reconciliationRevision)) return false;
    const renderedItemCount = Number.parseInt(
      this.querySelector('[ref="cartItemCount"]')?.textContent ?? '',
      10
    );
    if (Number.isFinite(renderedItemCount) && renderedItemCount >= 0) {
      this.#updateCartCount(renderedItemCount, revision);
    }
    if (feedback) this.#restoreProductAddError(feedback.productId, feedback.errorMessage);
    this.#updateCartQuantitySelectorButtonStates();
    this.#restoreComplementaryFocus(complementaryFocusState);
    if (this.#historyRestorePending) {
      this.#historyRestorePending = false;
      this.dispatchEvent(new CartSectionRestoredEvent());
    }
    return true;
  }

  /**
   * @param {any} cart
   * @param {{productId: string | undefined, errorMessage: string} | undefined} [feedback]
   */
  async reconcileCartData(cart, feedback) {
    if (!this.isConnected) return;
    if (this.#cartMutationInFlight || this.#pendingCartUpdateEvents > 0) {
      this.#queueOwedCartReconciliation({
        feedback,
        complementaryFocusState: this.#captureComplementaryFocus(),
      });
      return;
    }

    const revision = this.#cartUpdateRevision;
    const reconciliationRevision = ++this.#cartDataReconciliationRevision;
    try {
      await this.#applyReconciledCartData(
        cart,
        feedback,
        revision,
        reconciliationRevision,
        this.#captureComplementaryFocus()
      );
    } finally {
      this.#runOwedCartReconciliation();
    }
  }

  async fetchCartData() {
    const requestedRevision = ++this.#cartFetchRequestedRevision;

    while (this.#cartFetchCompletedRevision < requestedRevision) {
      if (!this.#pendingCartFetch) {
        const fetchLoop = this.#runCartFetchLoop();
        const trackedPromise = fetchLoop.finally(() => {
          if (this.#pendingCartFetch === trackedPromise) {
            this.#pendingCartFetch = null;
          }
        });
        this.#pendingCartFetch = trackedPromise;
      }

      await this.#pendingCartFetch;
    }

    return this.#latestCartData;
  }

  async #runCartFetchLoop() {
    while (this.#cartFetchCompletedRevision < this.#cartFetchRequestedRevision) {
      const targetRevision = this.#cartFetchRequestedRevision;
      let currentCart;

      try {
        const { data, response } = await fetchCartJSON(
          `${Theme.routes.cart_url}.json`,
          {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          },
          'Cart refresh'
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
        }

        currentCart = validateCart(data);
      } catch (error) {
        // If a later caller arrived while this GET was pending, still give that
        // caller its own trailing-edge attempt instead of returning the failed
        // or already-snapshotted request.
        if (targetRevision < this.#cartFetchRequestedRevision) continue;
        throw error;
      }

      this.#latestCartData = currentCart;
      this.#cartFetchCompletedRevision = targetRevision;
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
